import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  ThemeProvider,
  Stack,
  Alert,
  Snackbar,
  Typography,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ColDef } from "ag-grid-community";
import { RenderTable } from "../../common/splitview-table/RenderTable";
import { gridTheme } from "../../../utils/tableUtils";
import ButtonComponent from "../../common/button/Button";
import ErrorDialog from "../../common/ErrorDialog/ErrorDialog";
import Download from "../../common/DownloadGrid/Download";

import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { setApiCall } from "../../../redux/actions/userSlice";
import { hasRolePermission } from "../../../constants/roleAccess";
import HistoryIcon from "@mui/icons-material/History";
import {
  getReasonCodeList,
  manageApi,
  getHistoryApi,
} from "../../../api/reasonCodeApi";
import BulkPasteFilter from "../../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../../../components/common/FilterVariant/FilterManagerButton";

const DATE_FORMAT = "YYYY.MM.DD";

dayjs.extend(customParseFormat);

interface ReasonCodeRow {
  id: string;
  reasonCode: string;
  reasonDescription: string;
}

interface FormState {
  reasonCode?: string;
  reasonDescription: string;
}

const emptyForm = (): FormState => ({
  reasonDescription: "",
});

export default function ReasonCodeMaintenance() {
  const theme = useTheme();
  const { accounts } = useMsal();
  const dispatch = useDispatch();
  const gridRef = useRef<any>(null);
  const mountedRef = useRef(true);
    const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const historyGridRef = useRef<any>(null);
  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole ?? null,
  );
  const canEditSsaControl = hasRolePermission(currentRole, "edit_ssa_control");
  const canViewSsaHistory = hasRolePermission(currentRole, "view_ssa_history");

  const [loading, setLoading] = useState(false);

  // Dialog state

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  // Delete confirm

  // Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const [submitting, setSubmitting] = useState(false);
  const [isGridReady, setIsGridReady] = useState(false);
  const [tableRenderKey, setTableRenderKey] = useState(0);

  // Multi-plant add

  // Row selection for bulk ops
  const [selectedRows, setSelectedRows] = useState<ReasonCodeRow[]>([]);

  const [tableRows, setTableRows] = useState<ReasonCodeRow[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");

  const [form, setForm] = useState<FormState>(emptyForm());

  const [deletingRow, setDeletingRow] = useState<ReasonCodeRow | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);

  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const fitColumnsToWidth = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    try {
      api.sizeColumnsToFit();
    } catch {
      // Grid can briefly be in transition during mount/unmount.
    }
  }, []);

  const getUpdatedBy = (): string => {
    const primary = accounts?.[0] as any;
    return (
      primary?.idTokenClaims?.preferred_username ||
      primary?.username ||
      primary?.idTokenClaims?.email ||
      ""
    );
  };
  const openHistoryDialog = async (row: ReasonCodeRow) => {
    try {
      const response = await getHistoryApi(row.reasonCode);

      console.log("History API Response");
      console.log(response);

      if (Array.isArray(response)) {
        setHistoryRows(
          response.map((item: any) => ({
            id: String(item.historyId),
            historyId: item.historyId,
            reasonCode: item.reasonCode,
            oldReasonDescription: item.oldReasonDescription,
            newReasonDescription: item.newReasonDescription,
            actionType: item.actionType,
            changedBy: item.changedBy,
            changedAt: item.changedAt,
          })),
        );
      }

      setHistoryOpen(true);
    } catch (error) {
      console.error(error);
    }
  };
  const historyColumns: ColDef[] = [
    { headerName: "History ID", field: "historyId", hide: true },
    { headerName: "Reason Code", field: "reasonCode", hide: true },
    { headerName: "Old Description", field: "oldReasonDescription" },
    { headerName: "New Description", field: "newReasonDescription" },
    { headerName: "Action", field: "actionType" },
    { headerName: "Changed By", field: "changedBy" },
    { headerName: "Changed At", field: "changedAt" },
  ];

  const getSuccessMessageFromResponse = (
    response: any,
    fallback: string,
  ): string => {
    if (typeof response === "string" && response.trim()) return response;
    if (typeof response?.message === "string" && response.message.trim())
      return response.message;
    if (
      typeof response?.data?.message === "string" &&
      response.data.message.trim()
    ) {
      return response.data.message;
    }
    return fallback;
  };

  const toRows = (data: any): ReasonCodeRow[] => {
    const records = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : [];

    return records.map((item: any) => ({
      id: String(item.reasonCode),
      reasonCode: item.reasonCode,
      reasonDescription: item.reasonDescription,
      createdBy: item?.createdBy,
      createdAt: item?.createdAt,
    }));
  };

  const refreshTable = async () => {
    const data = await getReasonCodeList();

    if (!mountedRef.current) return;

    setTableRows(toRows(data));
  };

  const fetchData = useCallback(() => {
    mountedRef.current = true;
    setLoading(true);

    dispatch(setApiCall(true));

    getReasonCodeList()
      .then((data) => {
        if (mountedRef.current) {
          setTableRows(toRows(data));
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
        dispatch(setApiCall(false));
      });
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: "Reason Code",
        field: "reasonCode",
        hide: true,
      },
      {
        headerName: "Reason Description",
        field: "reasonDescription",
      },

      ...(canEditSsaControl
        ? [
            {
              headerName: "Action",
              field: "action",
              sortable: false,
              filter: false,
              cellRenderer: (params: any) => {
                const row = params.data;
                return (
                  <>
                    <IconButton
                      size="small"
                      title="Edit"
                      onClick={() => openEditDialog(row)}
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Delete"
                      onClick={() => openDeleteDialog(row)}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="History"
                      onClick={() => openHistoryDialog(row)}
                    >
                      <HistoryIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </>
                );
              },
            } as ColDef,
          ]
        : canViewSsaHistory
        ? [
            {
              headerName: "Action",
              field: "action",
              sortable: false,
              filter: false,
              cellRenderer: (params: any) => {
                const row = params.data;
                return (
                  <IconButton
                    size="small"
                    title="History"
                    onClick={() => openHistoryDialog(row)}
                  >
                    <HistoryIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                );
              },
            } as ColDef,
          ]
        : []),
    ],
    [canEditSsaControl, canViewSsaHistory],
  );
  // ── Dialog helpers ────────────────────────────────────────────────────────
  const openAddDialog = () => {
    setDialogMode("add");

    setForm({
      reasonDescription: "",
    });

    setDialogOpen(true);
  };

  const openEditDialog = (row: ReasonCodeRow) => {
    setDialogMode("edit");

    setForm({
      reasonCode: row.reasonCode,
      reasonDescription: row.reasonDescription,
    });

    setDialogOpen(true);
  };

  const openDeleteDialog = (row: ReasonCodeRow) => {
    setDeletingRow(row);

    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors: any = {};

    if (!form.reasonDescription?.trim()) {
      errors.reasonDescription = "Reason Description is required";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleDialogSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (dialogMode === "add") {
        await manageApi({
          action: "ADD",
          reasonDescription: form.reasonDescription.trim(),
          user: getUpdatedBy(),
        });

        setToastSeverity("success");
        setToastMessage("Reason Code created successfully");
      } else {
        await manageApi({
          action: "UPDATE",
          reasonCode: form.reasonCode,
          reasonDescription: form.reasonDescription.trim(),
          user: getUpdatedBy(),
        });

        setToastSeverity("success");
        setToastMessage("Reason Code updated successfully");
      }

      setToastOpen(true);
      setDialogOpen(false);

      setForm(emptyForm());
      setFormErrors({});

      await refreshTable();
    } catch (error: any) {
      console.error("Reason Code Save Error:", error);

      setToastSeverity("error");

      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to save reason code",
      );

      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRow) return;

    setSubmitting(true);

    try {
      const response = await manageApi({
        action: "DELETE",
        reasonCode: deletingRow.reasonCode,
        user: getUpdatedBy(),
      });

      setToastSeverity("success");
      setToastMessage(
        getSuccessMessageFromResponse(
          response,
          "Reason Code deleted successfully",
        ),
      );
      setToastOpen(true);

      setDeleteDialogOpen(false);
      setDeletingRow(null);

      await refreshTable();
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Delete failed",
      );
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetFilters = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
        setIsTriggerFilter(true);
    api.onFilterChanged();
  };

  useEffect(() => {
    if (!isGridReady) return;

    const timer = window.setTimeout(() => {
      fitColumnsToWidth();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isGridReady, columns, tableRows, fitColumnsToWidth]);

  useEffect(() => {
    if (!isGridReady) return;

    const handleResize = () => fitColumnsToWidth();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady, fitColumnsToWidth]);

  const isDialogSaveDisabled =
    submitting ||
    !form.reasonDescription?.trim() ||
    !!formErrors.reasonDescription;
  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ paddingLeft: 0, flex: 1 }}>
        {/* Header */}
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              textAlign: "left",
              marginLeft: 1,
            }}
          >
            Reason Code Maintenance
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {canEditSsaControl && (
              <ButtonComponent
                className="take-action"
                type="secondary"
                label="Add Reason Code"
                onClick={openAddDialog}
                borderRadius="28px"
              />
            )}

            {(tableRows?.length ?? 0) > 0 && (
              <><ButtonComponent
                className="take-action"
                type="secondary"
                label="Reset Filters"
                onClick={handleResetFilters}
                borderRadius="28px"
                width="auto"
                maxWidth="none"
              /><FilterManagerButton
                  gridRef={gridRef}
                  tableName="Reason Code Maintenance"
                  userEmail={localStorage.getItem("userEmail") || ""}
                  disabled={!tableRows?.length}
                />
              {/* <BulkPasteFilter
                  gridRef={gridRef}
                  columns={columns}
                  triggerClearFilter = {isTriggerFilter}
                /> */}
                </>
            )}

            <Download
              loading={loading}
              gridRef={gridRef}
              fileName="Reason Code Maintenance"
              rowData={tableRows}
            />
          </Stack>
        </Stack>

        {/* Add/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === "add" ? "Add Reason Code" : "Edit Reason Code"}
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {dialogMode === "edit" && (
                <TextField
                  sx={{ display: "none" ,
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                  label="Reason Code"
                  size="small"
                  value={form.reasonCode ?? ""}
                  disabled
                  fullWidth
                />
              )}

              <TextField
                label="Reason Description"
                value={form.reasonDescription}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    reasonDescription: e.target.value,
                  }))
                }
                size="small"
                multiline
                rows={3}
                fullWidth
                required
                sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                error={!!formErrors.reasonDescription}
                helperText={formErrors.reasonDescription}
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />

            <ButtonComponent
              className="take-action"
              type="secondary"
              label={submitting ? "Saving..." : "Save"}
              onClick={handleDialogSubmit}
              disabled={isDialogSaveDisabled}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography>
              Are you sure you want to delete reason code{" "}
              
            </Typography>
            <Typography sx={{ fontWeight: 600, mt: 1 }}><strong>{deletingRow?.reasonDescription}</strong> ?</Typography>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />

            <ButtonComponent
              className="take-action"
              type="secondary"
              label={submitting ? "Deleting..." : "Delete"}
              onClick={handleDeleteConfirm}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>
        {/* History Dialog */}
        <Dialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Reason Code History</DialogTitle>

          <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <RenderTable
              loading={loading}
              gridRef={historyGridRef}
              gridTheme={gridTheme}
              columns={historyColumns}
              rows={historyRows ?? []}
              rowsPerPage={5}
              resizable={true}
              setGridReady={() => {}}
              sideBar={{}}
              minimized={null}
              emitEvent={() => {}}
              onChange={() => {}}
              isSelectedRow={false}
              setSelectedRow={() => {}}
            />
          </DialogContent>

          <DialogActions>
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Close"
              onClick={() => setHistoryOpen(false)}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Error Dialog */}
        <ErrorDialog
          open={toastOpen && toastSeverity === "error"}
          message={toastMessage}
          onClose={() => setToastOpen(false)}
        />

        {/* Success Snackbar */}
        <Snackbar
          open={toastOpen && toastSeverity !== "error"}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Alert
            severity={toastSeverity}
            variant="filled"
            onClose={() => setToastOpen(false)}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>

        {/* Main Grid */}
        <RenderTable
          key={tableRenderKey}
          loading={loading}
          gridRef={gridRef}
          gridTheme={gridTheme}
          columns={columns}
          rows={tableRows}
          rowsPerPage={10}
          setGridReady={() => {
            setIsGridReady(true);
            fitColumnsToWidth();
          }}
          sideBar={{}}
          minimized={null}
          emitEvent={() => {}}
          onChange={() => {}}
          isSelectedRow={false}
        />
      </Stack>
    </ThemeProvider>
  );
}
